// Shared "grouped stage list" renderer for any Lifecycle dropdown/submenu — splits
// stages into "Lifecycle Stages" (active) and "Lost Stages" sections with small
// muted headers, matching the reference layout Rahim provided (2026-08-19). Used by
// both LifecycleStageChip.jsx (the inline card chip) and MessageCard.jsx's "..."
// context-menu submenu, so both pickers look identical instead of drifting apart.
// Renders MenuItem/Typography children only — the caller owns the actual <Menu>
// (anchor positioning differs between the two contexts).
//
// Deliberately no "Clear selection" footer yet — the backend rejects an empty
// toStageId today (moveContactLifecycleStage.service.js), so that's a separate
// small backend+frontend task, skipped for this pass per Rahim's explicit call.
import React from "react";
import { Box, MenuItem, Typography } from "@mui/material";

// Header + item rows share ONE left offset (WRAPPER_PADDING + ROW_INDENT below) —
// same "outer padding wrapper, not per-row margin" pattern as LeftNav.jsx's own rail
// popups (railPopupItemSx / the Box p:0.75 wrapping it in LeftNav.jsx), confirmed as
// this app's real convention for hover pills that float inset from the popup's own
// edges instead of touching them (Rahim's correction, 2026-08-19 — per-row `mx` had
// made the hover background go edge-to-edge).
const WRAPPER_PADDING = 0.75; // matches LeftNav.jsx's rail-popup item wrapper exactly
const ROW_INDENT = 1.25;

const SECTION_HEADER_SX = {
  pl: ROW_INDENT,
  pr: 1,
  pt: 1,
  pb: 0.5,
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "text.secondary",
};

function LifecycleStageMenuList({ stages, currentStageId, onSelect, itemSx, selectedSx, disabled = false }) {
  const activeStages = stages.filter((s) => s.stageType !== "lost");
  const lostStages = stages.filter((s) => s.stageType === "lost");

  const renderStageItem = (stage) => (
    <MenuItem
      key={stage._id}
      disabled={disabled}
      selected={String(stage._id) === String(currentStageId)}
      onClick={(e) => onSelect(e, stage._id)}
      dense
      sx={{
        ...itemSx,
        // No mx here — the outer Box wrapper below (WRAPPER_PADDING) is what keeps
        // the hover/selected pill off the popup's edges, matching LeftNav's convention.
        // pl pinned to ROW_INDENT so it lines up with SECTION_HEADER_SX's own pl exactly.
        mx: 0,
        pl: ROW_INDENT,
        "&.Mui-selected": selectedSx,
        "&.Mui-selected:hover": selectedSx,
      }}
    >
      {stage.icon ? (
        <Box component="span" sx={{ fontSize: "0.9375rem", lineHeight: 1 }}>{stage.icon}</Box>
      ) : (
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: stage.color || "#64748b" }} />
      )}
      {stage.name}
    </MenuItem>
  );

  return (
    <Box sx={{ p: WRAPPER_PADDING }}>
      {activeStages.length > 0 && (
        <>
          <Typography sx={SECTION_HEADER_SX}>Lifecycle Stages</Typography>
          {activeStages.map(renderStageItem)}
        </>
      )}
      {lostStages.length > 0 && (
        <>
          <Typography sx={SECTION_HEADER_SX}>Lost Stages</Typography>
          {lostStages.map(renderStageItem)}
        </>
      )}
    </Box>
  );
}

export default LifecycleStageMenuList;
